import type { TagsMenuOption } from './tags.types';

/**
 * A flat option as produced by `DynamicOptionsService.mapEntriesToOptions`. When the
 * field's `optionsArray` carries a `groupByField`, every option gets a `group` label
 * (see `extractGroupInfo` in dynamic-options.service.ts) — a plain string, not a nesting.
 */
export interface GroupedFlatOption {
  label: string;
  value: unknown;
  group?: string;
}

/**
 * Folds the flat `group` label that `groupByField` produces into the one-level
 * `children[]` shape the tags overlay renders as a drilldown (land → regio's).
 *
 * The two sides never met before: `groupByField` was built for the autocomplete, which
 * renders groups as flat headers, while `tags.component.ts` only ever read `children[]`
 * and had no producer. This is that bridge, so a "Regio's" module with a country field
 * can drive the nested picker without a new data model.
 *
 * Rules:
 * - Groups come first, sorted by label; ungrouped options follow in source order.
 *   Source order cannot be used for the groups themselves: the backend sorts by the
 *   OPTION label, so grouping by country would list "Duitsland" before "België"
 *   (Baden-Württemberg sorts before Brussel) — useless for finding your own country.
 * - Children keep the order they arrived in, which is the backend's sort.
 * - The parent's `value` is the group label itself. That value is what
 *   `selectAllInParent` stores for "Alles in {parent}", so it must be stable and
 *   human-readable — a cold reload renders it verbatim as the chip label.
 * - A group of one still becomes a parent: a country with a single region must not
 *   silently render as a flat row while its siblings drill down.
 */
export function nestOptionsByGroup<T extends GroupedFlatOption>(options: readonly T[]): TagsMenuOption[] {
  if (!Array.isArray(options) || options.length === 0) {
    return [];
  }

  type ParentOption = TagsMenuOption & { children: Array<{ label: string; value: unknown }> };

  const parentsByGroup = new Map<string, ParentOption>();
  const ongegroepeerd: TagsMenuOption[] = [];

  for (const option of options) {
    const group = typeof option?.group === 'string' ? option.group.trim() : '';

    if (!group) {
      // Ungrouped options keep the object they arrived in — rebuilding it would drop the
      // extra keys other code reads off an option (additionalInfo, disabled,
      // unavailableReason, description, recommended, …).
      ongegroepeerd.push(option as unknown as TagsMenuOption);
      continue;
    }

    const existing = parentsByGroup.get(group);
    if (existing) {
      existing.children.push({ label: option.label, value: option.value });
      continue;
    }

    parentsByGroup.set(group, {
      label: group,
      value: group,
      children: [{ label: option.label, value: option.value }],
    });
  }

  const parents = [...parentsByGroup.values()].sort((a, b) => a.label.localeCompare(b.label, 'nl'));
  return [...parents, ...ongegroepeerd];
}
